import { lazy, Suspense } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { BarChart3, Clock, Megaphone, Package, TrendingDown, Users } from 'lucide-react';
import { Alert } from '@/components/ui/Alert';
import { Badge } from '@/components/ui/Badge';
import { CardTableArea } from '@/components/ui/Card';
import { ChartAreaSkeleton, ChartCard } from '@/components/ui/ChartCard';
import { KpiCard } from '@/components/ui/KpiCard';
import { Select } from '@/components/ui/Select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeaderCell,
  TableRow,
} from '@/components/ui/Table';
import { ReportFilters, useReportFiltros } from '@/features/reports/ReportFilters';
import { HeatmapGrid } from '@/features/reports/charts/HeatmapGrid';
import {
  CAMPOS_HORARIO,
  INTERVALOS_SERIE,
  KPIS_EN_ESPERA,
  LIMITES_PRODUCTOS,
  METRICAS_SERIE,
  PARAM_HORARIO,
  PARAM_METRICA,
  agruparFrom,
  avisoProductosTruncado,
  avisosSerieReporte,
  campoHorarioFrom,
  countLabel,
  datosPublicidad,
  descripcionSerie,
  describirPeriodoEfectivo,
  describirPico,
  filasCanalReporte,
  filasEstadoLeadReporte,
  filasEstadoOrdenReporte,
  filasEstadoSinOrdenReporte,
  filasMotivosReporte,
  filasProductosReporte,
  filasPublicidadReporte,
  filasSerieReporte,
  formatCount,
  formatNumber,
  hayFiltros,
  horasMasActivas,
  intervaloFrom,
  limiteProductosFrom,
  metricaFrom,
  metricaSerie,
  ordenarFrom,
  resumenSerie,
  totalesPublicidadReporte,
  totalesSerieReporte,
} from '@/features/reports/reports-format';
import { kpisResumen } from '@/features/reports/reports-blocks';
import {
  useReportCampanas,
  useReportHorarios,
  useReportMotivosNoCierre,
  useReportProductos,
  useReportResumen,
  useReportSerie,
  useZonaHorariaDeRespuesta,
} from '@/features/reports/use-reports';
import { useActiveTenantAccess } from '@/hooks/use-active-tenant';
import { getErrorMessage } from '@/lib/api-error';
import { REPORT_PARAMS } from '@/api/reports';
import { ROUTES } from '@/routes/paths';

/**
 * Reportes (`/reports`, lo que faltaba del paso 8): las SEIS tablas del módulo 10.
 *
 * Qué es y qué no es:
 *  - ES la pantalla de detalle del negocio activo: cada bloque responde con filas lo que el panel
 *    enseña con una cifra. Lo que acá se lee es lo que se audita — el CPA fila por fila, el reparto
 *    por canal, los puntos de la serie, el ranking de productos, por qué no cerró y qué horas se
 *    trabaja;
 *  - NO recalcula el negocio. Los números son los del backend, con tres únicas cuentas hechas en
 *    pantalla (el % por canal, el % de las conversaciones sin orden y el % del mapa de horarios,
 *    porque de esos tres el backend solo manda el conteo) y todas rotuladas como tales;
 *  - NO esconde los avisos: `truncado`, `truncadoRango`, `fueraDeRango` y los `avisos` de publicidad
 *    se muestran tal cual llegan, porque un reporte incompleto que no lo dice es peor que no tenerlo;
 *  - NO convierte `null` en 0: «—» con su motivo (`sin ventas en el periodo`, `sin gasto cargado`)
 *    donde el dato no se puede calcular.
 *
 * Los seis bloques y su endpoint (`docs/REPORTES.md` §6):
 *  1. Resumen del periodo .... `GET .../reports/resumen`          (KPIs + canal + leads + órdenes)
 *  2. Evolución del periodo .. `GET .../reports/serie`            (gráfico + punto por punto)
 *  3. Publicidad y CPA ....... `GET .../reports/campanas`         (por anuncio o por campaña)
 *  4. Productos más vendidos . `GET .../reports/productos`        (ranking, `limite` y `ordenar`)
 *  5. Por qué no cerró ....... `GET .../reports/motivos-no-cierre`
 *  6. Horarios de atención ... `GET .../reports/horarios`         (mapa 7×24 + franjas pico)
 *
 * Comparte TODO con el panel: la barra de filtros y los cinco KPIs (`ReportFilters`,
 * `reports-blocks.tsx`), los helpers de formato y las filas de las tablas (`reports-format.ts`), los
 * hooks (`use-reports.ts`) y el mapa de calor (`charts/HeatmapGrid`). El gráfico de la serie viaja con
 * `React.lazy` igual que en el panel, así que abrir un reporte en un móvil no descarga recharts hasta
 * que hace falta.
 */
const SeriesChart = lazy(() =>
  import('@/features/reports/charts/SeriesChart').then((modulo) => ({
    default: modulo.SeriesChart,
  })),
);

/** Opciones del selector de orden del ranking de productos (los dos del backend). */
const ORDENES_PRODUCTOS = [
  { value: 'cantidad', label: 'Por unidades vendidas' },
  { value: 'ingresos', label: 'Por ingresos' },
] as const;

/** Opciones del selector de agrupación de publicidad (los dos del backend). */
const AGRUPACIONES = [
  { value: 'anuncio', label: 'Por anuncio' },
  { value: 'campania', label: 'Por campaña' },
] as const;

export function ReportsPage() {
  const { tenantId } = useActiveTenantAccess();
  const [searchParams] = useSearchParams();
  const { filtros, rango, setFiltros, setParam, resetFiltros, zonaHoraria } = useReportFiltros();

  const intervalo = intervaloFrom(searchParams);
  const metrica = metricaFrom(searchParams);
  const agrupar = agruparFrom(searchParams);
  const campoHorario = campoHorarioFrom(searchParams);
  const ordenar = ordenarFrom(searchParams);
  const limite = limiteProductosFrom(searchParams);

  const resumenQuery = useReportResumen({ tenantId, filtros, zonaHoraria });
  const serieQuery = useReportSerie({ tenantId, filtros, zonaHoraria, intervalo });
  const campanasQuery = useReportCampanas({ tenantId, filtros, zonaHoraria, agrupar });
  const productosQuery = useReportProductos({ tenantId, filtros, zonaHoraria, limite, ordenar });
  const motivosQuery = useReportMotivosNoCierre({ tenantId, filtros, zonaHoraria });
  const horariosQuery = useReportHorarios({ tenantId, filtros, zonaHoraria });

  const resumen = resumenQuery.data;
  const serie = serieQuery.data;
  const campanas = campanasQuery.data;
  const productos = productosQuery.data;
  const motivos = motivosQuery.data;
  const horarios = horariosQuery.data;

  // La zona del negocio se aprende de la primera respuesta (ver `useZonaHorariaDeRespuesta`).
  useZonaHorariaDeRespuesta(
    serie?.zonaHoraria ?? horarios?.zonaHoraria ?? resumen?.periodo.zonaHoraria,
  );

  const metricaDef = metricaSerie(metrica);
  const periodo =
    resumen?.periodo ??
    serie?.periodo ??
    horarios?.periodo ??
    campanas?.periodo ??
    productos?.periodo ??
    motivos?.periodo ??
    null;
  const enlacePanel = `${ROUTES.dashboard}${searchParams.toString() ? `?${searchParams}` : ''}`;

  /* Las filas de cada bloque se arman acá (y no dentro del JSX) para que el render se lea de un tirón. */
  const resumenVacio = Boolean(
    resumen &&
      resumen.conversaciones.total === 0 &&
      resumen.mensajes.total === 0 &&
      resumen.ordenes.total === 0,
  );
  const resumenDeLaSerie = serie
    ? resumenSerie(serie.puntos, metrica, intervalo, zonaHoraria)
    : null;
  const filasSerie = serie ? filasSerieReporte(serie.puntos, intervalo, zonaHoraria) : [];
  const totalesSerie = serie ? totalesSerieReporte(serie.puntos) : null;
  const filasPublicidad = campanas ? filasPublicidadReporte(campanas.items) : [];
  const totalesPublicidad = campanas ? totalesPublicidadReporte(campanas.totales) : null;
  const filasProductos = productos ? filasProductosReporte(productos.productos) : [];
  const filasMotivos = motivos ? filasMotivosReporte(motivos.motivos) : [];
  const franjasPico = horarios ? horasMasActivas(horarios.matriz, campoHorario) : [];
  const avisoProductos = productos ? avisoProductosTruncado(productos) : null;

  return (
    <div className="space-y-6">
      <ReportFilters
        filtros={filtros}
        rango={rango}
        zonaHoraria={zonaHoraria}
        onChange={setFiltros}
        onReset={resetFiltros}
        actions={
          <Link
            to={enlacePanel}
            className="text-caption font-medium text-brand-700 hover:underline"
          >
            Ver el panel
          </Link>
        }
      />

      {periodo ? (
        <p className="-mt-3 text-caption text-ink-faint">
          {hayFiltros(filtros)
            ? `Las seis tablas de abajo cuentan ${describirPeriodoEfectivo(periodo, zonaHoraria)}.`
            : `Sin fechas elegidas el backend usa ${describirPeriodoEfectivo(
                periodo,
                zonaHoraria,
              )}: elige un rango arriba para cambiarlo.`}
        </p>
      ) : null}

      {/* 1 ---------------------------------------------------------- Resumen --- */}
      <ChartCard
        title="Resumen del periodo"
        description="Los cinco indicadores del periodo y, debajo, de dónde salieron: el reparto por canal, los leads por estado y las órdenes por estado."
        error={resumenQuery.isError ? getErrorMessage(resumenQuery.error) : null}
        onRetry={() => void resumenQuery.refetch()}
      >
        {resumenQuery.isError ? null : resumen ? (
          <div className="space-y-6">
            {resumenVacio ? (
              <Alert tone="info">
                El periodo no tiene actividad: no hubo conversaciones, mensajes ni órdenes. Prueba con
                un rango más amplio o quita los filtros.
              </Alert>
            ) : null}

            <section
              aria-label="Indicadores del periodo"
              className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5"
            >
              {kpisResumen(resumen).map((kpi) => (
                <KpiCard key={kpi.label} {...kpi} />
              ))}
            </section>

            <div className="grid gap-6 lg:grid-cols-3">
              <section>
                <h4 className="mb-2 text-caption font-semibold text-ink">
                  Conversaciones por canal
                </h4>
                <CardTableArea>
                  <Table>
                    <TableHead>
                      <TableRow>
                        <TableHeaderCell>Canal</TableHeaderCell>
                        <TableHeaderCell align="right">Conversaciones</TableHeaderCell>
                        <TableHeaderCell align="right">% del periodo</TableHeaderCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {filasCanalReporte(resumen.conversaciones).map((fila) => (
                        <TableRow key={fila.id}>
                          <TableCell>{fila.label}</TableCell>
                          <TableCell align="right" className="tabular">
                            {formatCount(fila.total)}
                          </TableCell>
                          <TableCell align="right" className="tabular">
                            {fila.porcentaje}
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </CardTableArea>
              </section>

              <section>
                <h4 className="mb-2 text-caption font-semibold text-ink">Leads por estado</h4>
                <CardTableArea>
                  <Table>
                    <TableHead>
                      <TableRow>
                        <TableHeaderCell>Estado</TableHeaderCell>
                        <TableHeaderCell align="right">Conversaciones</TableHeaderCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {filasEstadoLeadReporte(resumen.conversaciones.porEstadoLead).map((fila) => (
                        <TableRow key={fila.id}>
                          <TableCell>
                            <Badge tone={fila.tone} dot>
                              {fila.label}
                            </Badge>
                          </TableCell>
                          <TableCell align="right" className="tabular">
                            {formatCount(fila.total)}
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </CardTableArea>
                <p className="mt-2 text-micro text-ink-faint">
                  El estado del lead lo mantiene el CRM; acá solo se cuenta lo del periodo.
                </p>
              </section>

              <section>
                <h4 className="mb-2 text-caption font-semibold text-ink">Órdenes por estado</h4>
                <CardTableArea>
                  <Table>
                    <TableHead>
                      <TableRow>
                        <TableHeaderCell>Estado</TableHeaderCell>
                        <TableHeaderCell align="right">Órdenes</TableHeaderCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {filasEstadoOrdenReporte(resumen.ordenes.porEstado).map((fila) => (
                        <TableRow key={fila.id}>
                          <TableCell>
                            <Badge tone={fila.tone} dot>
                              {fila.label}
                            </Badge>
                          </TableCell>
                          <TableCell align="right" className="tabular">
                            {formatCount(fila.total)}
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                    <tfoot>
                      <TableRow className="border-t border-line bg-surface-muted/60">
                        <TableCell className="font-semibold text-ink">Total de órdenes</TableCell>
                        <TableCell align="right" className="tabular font-semibold text-ink">
                          {formatCount(resumen.ordenes.total)}
                        </TableCell>
                      </TableRow>
                    </tfoot>
                  </Table>
                </CardTableArea>
                <p className="mt-2 text-micro text-ink-faint">
                  Una «venta» es una orden confirmada, en preparación, enviada o entregada (
                  {countLabel(resumen.ordenes.ventasConfirmadas, 'venta', 'ventas')} en el periodo).
                </p>
              </section>
            </div>

            <p className="text-micro text-ink-faint">
              El «% del periodo» de la primera tabla lo calcula esta pantalla: el backend solo manda
              el conteo por canal. Los «—» son datos que no se pueden calcular (un periodo sin
              conversaciones no se reparte en porcentajes), nunca un cero.
            </p>
          </div>
        ) : (
          <div className="space-y-6">
            <section
              aria-label="Indicadores del periodo"
              className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5"
            >
              {KPIS_EN_ESPERA.map((etiqueta, indice) => (
                <KpiCard key={indice} label={etiqueta} value="" loading />
              ))}
            </section>

            <ChartAreaSkeleton height={200} />
          </div>
        )}
      </ChartCard>

      {/* 2 ------------------------------------------------------- Evolución --- */}
      <ChartCard
        title="Evolución del periodo"
        description={
          <>
            <span className="inline-flex items-center gap-1.5">
              <BarChart3 size={14} aria-hidden="true" />
              {resumenDeLaSerie
                ? descripcionSerie(resumenDeLaSerie, metricaDef, intervalo)
                : 'La serie del periodo, punto por punto.'}
            </span>
          </>
        }
        actions={
          <>
            <Select
              aria-label="Métrica del gráfico"
              className="w-40"
              value={metrica}
              onChange={(event) => setParam(PARAM_METRICA, event.target.value)}
            >
              {METRICAS_SERIE.map((opcion) => (
                <option key={opcion.id} value={opcion.id}>
                  {opcion.corto}
                </option>
              ))}
            </Select>

            <Select
              aria-label="Intervalo del gráfico"
              className="w-40"
              value={intervalo}
              onChange={(event) => setParam(REPORT_PARAMS.intervalo, event.target.value)}
            >
              {INTERVALOS_SERIE.map((opcion) => (
                <option key={opcion.value} value={opcion.value}>
                  {opcion.label}
                </option>
              ))}
            </Select>
          </>
        }
        loading={serieQuery.isPending && !serie}
        error={serieQuery.isError ? getErrorMessage(serieQuery.error) : null}
        onRetry={() => void serieQuery.refetch()}
        height={360}
        empty={
          serie && serie.puntos.length === 0
            ? {
                title: 'La serie no tiene puntos',
                description:
                  'El backend no devolvió ningún punto para este rango e intervalo: prueba con un rango más amplio.',
              }
            : null
        }
      >
        {serie ? (
          <div className="space-y-6">
            {avisosSerieReporte(serie).map((aviso) => (
              <Alert key={aviso} tone="warning">
                {aviso}
              </Alert>
            ))}

            <Suspense fallback={<ChartAreaSkeleton height={300} />}>
              <SeriesChart
                puntos={serie.puntos}
                metrica={metrica}
                intervalo={serie.intervalo}
                zonaHoraria={serie.zonaHoraria}
                height={300}
              />
            </Suspense>

            <CardTableArea>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableHeaderCell>Periodo</TableHeaderCell>
                    <TableHeaderCell align="right">Conversaciones</TableHeaderCell>
                    <TableHeaderCell align="right">Mensajes</TableHeaderCell>
                    <TableHeaderCell align="right">Órdenes</TableHeaderCell>
                    <TableHeaderCell align="right">Ventas confirmadas</TableHeaderCell>
                    <TableHeaderCell align="right">Ingresos</TableHeaderCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {filasSerie.map((fila) => (
                    <TableRow key={fila.id}>
                      <TableCell className="whitespace-nowrap" title={fila.etiquetaCorta}>
                        {fila.etiqueta}
                      </TableCell>
                      <TableCell align="right" className="tabular">
                        {formatCount(fila.conversaciones)}
                      </TableCell>
                      <TableCell align="right" className="tabular">
                        {formatCount(fila.mensajes)}
                      </TableCell>
                      <TableCell align="right" className="tabular">
                        {formatCount(fila.ordenes)}
                      </TableCell>
                      <TableCell align="right" className="tabular">
                        {formatCount(fila.ventasConfirmadas)}
                      </TableCell>
                      <TableCell align="right" className="tabular">
                        {formatNumber(fila.ingresos)}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>

                {totalesSerie ? (
                  <tfoot>
                    <TableRow className="border-t border-line bg-surface-muted/60">
                      <TableCell className="font-semibold text-ink">
                        {countLabel(totalesSerie.puntos, 'punto', 'puntos')}
                      </TableCell>
                      <TableCell align="right" className="tabular font-semibold text-ink">
                        {formatCount(totalesSerie.conversaciones)}
                      </TableCell>
                      <TableCell align="right" className="tabular font-semibold text-ink">
                        {formatCount(totalesSerie.mensajes)}
                      </TableCell>
                      <TableCell align="right" className="tabular font-semibold text-ink">
                        {formatCount(totalesSerie.ordenes)}
                      </TableCell>
                      <TableCell align="right" className="tabular font-semibold text-ink">
                        {formatCount(totalesSerie.ventasConfirmadas)}
                      </TableCell>
                      <TableCell align="right" className="tabular font-semibold text-ink">
                        {formatNumber(totalesSerie.ingresos)}
                      </TableCell>
                    </TableRow>
                  </tfoot>
                ) : null}
              </Table>
            </CardTableArea>

            <p className="text-micro text-ink-faint">
              La fila del pie es la suma de los puntos de ESTA tabla, no el total del periodo: el
              total del negocio es el del resumen del primer bloque. Si el backend truncó la serie,
              los dos números no cuadran a propósito y el aviso de arriba dice por qué.
            </p>
          </div>
        ) : null}
      </ChartCard>

      {/* 3 ------------------------------------------------ Publicidad y CPA --- */}
      <ChartCard
        title="Publicidad y CPA"
        description={
          <>
            <span className="inline-flex items-center gap-1.5">
              <Megaphone size={14} aria-hidden="true" />
              Quién trajo las conversaciones y a qué precio: el gasto que se carga en Campañas, las
              ventas confirmadas y el CPA de cada fila. La fila «Sin atribución» es lo que entró por
              fuera de la pauta (orgánico o cargado a mano).
            </span>
          </>
        }
        actions={
          <>
            <Link
              to={ROUTES.campaigns}
              className="text-caption font-medium text-brand-700 hover:underline"
            >
              Ver el gasto cargado
            </Link>

            <Select
              aria-label="Agrupar la publicidad"
              className="w-40"
              value={agrupar}
              onChange={(event) => setParam(REPORT_PARAMS.agrupar, event.target.value)}
            >
              {AGRUPACIONES.map((opcion) => (
                <option key={opcion.value} value={opcion.value}>
                  {opcion.label}
                </option>
              ))}
            </Select>
          </>
        }
        loading={campanasQuery.isPending && !campanas}
        error={campanasQuery.isError ? getErrorMessage(campanasQuery.error) : null}
        onRetry={() => void campanasQuery.refetch()}
      >
        {campanas ? (
          <div className="space-y-6">
            <section
              aria-label="Gasto y CPA del periodo"
              className="grid gap-4 sm:grid-cols-2 xl:grid-cols-4"
            >
              {datosPublicidad(campanas.totales).map((dato) => (
                <KpiCard key={dato.id} label={dato.label} value={dato.valor} hint={dato.hint} />
              ))}
            </section>

            {campanas.avisos.map((aviso) => (
              <Alert key={aviso} tone="warning">
                {aviso}
              </Alert>
            ))}

            <CardTableArea>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableHeaderCell>Fila</TableHeaderCell>
                    <TableHeaderCell>Origen</TableHeaderCell>
                    <TableHeaderCell align="right">Conversaciones</TableHeaderCell>
                    <TableHeaderCell align="right">Clientes</TableHeaderCell>
                    <TableHeaderCell align="right">Órdenes</TableHeaderCell>
                    <TableHeaderCell align="right">Ingresos</TableHeaderCell>
                    <TableHeaderCell align="right">Gasto</TableHeaderCell>
                    <TableHeaderCell align="right">CPA</TableHeaderCell>
                    <TableHeaderCell align="right">Conversión</TableHeaderCell>
                    <TableHeaderCell align="right">Ticket</TableHeaderCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {filasPublicidad.map((fila) => (
                    <TableRow key={fila.id}>
                      <TableCell>
                        <span className="block font-medium text-ink">{fila.label}</span>
                        <span className="block text-micro text-ink-faint">{fila.ids}</span>
                      </TableCell>
                      <TableCell>
                        <Badge tone={fila.origenTone} dot>
                          {fila.origen}
                        </Badge>
                      </TableCell>
                      <TableCell align="right" className="tabular">
                        {formatCount(fila.conversaciones)}
                      </TableCell>
                      <TableCell align="right" className="tabular">
                        {formatCount(fila.clientes)}
                      </TableCell>
                      <TableCell align="right" className="tabular">
                        {formatCount(fila.ordenes)}
                        <span className="block text-micro text-ink-faint">
                          {countLabel(fila.ordenesConfirmadas, 'confirmada', 'confirmadas')}
                        </span>
                      </TableCell>
                      <TableCell align="right" className="tabular">
                        {formatNumber(fila.ingresos)}
                      </TableCell>
                      <TableCell align="right" className="tabular">
                        {formatNumber(fila.gasto)}
                        {fila.gastoEnCampania ? (
                          <span className="block text-micro text-ink-faint">
                            su campaña tiene gasto aparte
                          </span>
                        ) : null}
                      </TableCell>
                      <TableCell align="right" className="tabular">
                        {fila.cpa}
                      </TableCell>
                      <TableCell align="right" className="tabular">
                        {fila.tasa}
                      </TableCell>
                      <TableCell align="right" className="tabular">
                        {fila.ticket}
                      </TableCell>
                    </TableRow>
                  ))}

                  {/* Sin filas, la tabla igual se pinta (la cabecera y el pie son el reporte); la
                      explicación va en una fila para no perder las cuatro cifras del bloque. */}
                  {filasPublicidad.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={10} className="text-center text-ink-muted">
                        Ninguna fila de publicidad en el periodo: no hubo conversaciones con
                        atribución ni filas de gasto cargadas. Si tienes campañas en Meta, cárgalas en
                        Campañas (o usa «Descubrir anuncios») para que el CPA tenga con qué
                        calcularse.
                      </TableCell>
                    </TableRow>
                  ) : null}
                </TableBody>

                {totalesPublicidad ? (
                  <tfoot>
                    <TableRow className="border-t border-line bg-surface-muted/60">
                      <TableCell className="font-semibold text-ink">
                        Total del periodo ({countLabel(filasPublicidad.length, 'fila', 'filas')})
                      </TableCell>
                      <TableCell />
                      <TableCell align="right" className="tabular font-semibold text-ink">
                        {formatCount(totalesPublicidad.conversaciones)}
                      </TableCell>
                      <TableCell align="right" className="tabular font-semibold text-ink">
                        {formatCount(totalesPublicidad.clientes)}
                      </TableCell>
                      <TableCell align="right" className="tabular font-semibold text-ink">
                        {formatCount(totalesPublicidad.ordenes)}
                      </TableCell>
                      <TableCell align="right" className="tabular font-semibold text-ink">
                        {formatNumber(totalesPublicidad.ingresos)}
                      </TableCell>
                      <TableCell align="right" className="tabular font-semibold text-ink">
                        {formatNumber(totalesPublicidad.gasto)}
                      </TableCell>
                      <TableCell align="right" className="tabular font-semibold text-ink">
                        {totalesPublicidad.cpa}
                      </TableCell>
                      <TableCell align="right" className="tabular font-semibold text-ink">
                        {totalesPublicidad.tasa}
                      </TableCell>
                      <TableCell align="right" className="tabular font-semibold text-ink">
                        {totalesPublicidad.ticket}
                      </TableCell>
                    </TableRow>
                  </tfoot>
                ) : null}
              </Table>
            </CardTableArea>

            {totalesPublicidad ? (
              <p className="text-micro text-ink-faint">
                Gasto atribuido {formatNumber(totalesPublicidad.gasto)} · gasto registrado{' '}
                {formatNumber(totalesPublicidad.gastoTotalRegistrado)} (todas las campañas activas,
                atribuidas o no) · CPA global {totalesPublicidad.cpaGlobal}, que es el que incluye ese
                gasto suelto. No se reparte ni se prorratea nada: cada fila lleva el gasto que tiene
                cargado, y por eso un «—» en el CPA significa «sin ventas en el periodo» o «sin gasto
                cargado», nunca un cero.
              </p>
            ) : null}
          </div>
        ) : null}
      </ChartCard>

      {/* 4 -------------------------------------------- Productos más vendidos --- */}
      <ChartCard
        title="Productos más vendidos"
        description={
          <>
            <span className="inline-flex items-center gap-1.5">
              <Package size={14} aria-hidden="true" />
              Unidades e ingresos por producto de las órdenes confirmadas del periodo. El backend
              agrupa por nombre sin distinguir mayúsculas, así que «Camiseta roja» y «camiseta ROJA»
              son la misma fila.
            </span>
          </>
        }
        actions={
          <>
            <Select
              aria-label="Ordenar el ranking de productos"
              className="w-48"
              value={ordenar}
              onChange={(event) => setParam(REPORT_PARAMS.ordenar, event.target.value)}
            >
              {ORDENES_PRODUCTOS.map((opcion) => (
                <option key={opcion.value} value={opcion.value}>
                  {opcion.label}
                </option>
              ))}
            </Select>

            <Select
              aria-label="Cuántos productos listar"
              className="w-36"
              value={String(limite)}
              onChange={(event) => setParam(REPORT_PARAMS.limite, event.target.value)}
            >
              {LIMITES_PRODUCTOS.map((valor) => (
                <option key={valor} value={valor}>
                  {valor} productos
                </option>
              ))}
            </Select>
          </>
        }
        loading={productosQuery.isPending && !productos}
        error={productosQuery.isError ? getErrorMessage(productosQuery.error) : null}
        onRetry={() => void productosQuery.refetch()}
        height={240}
        empty={
          productos && productos.productos.length === 0
            ? {
                title: 'Ningún producto vendido en el periodo',
                description:
                  'El periodo no tiene ventas confirmadas con productos cargados, así que no hay nada que rankear.',
              }
            : null
        }
      >
        {productos ? (
          <div className="space-y-6">
            {avisoProductos ? (
              <Alert tone="warning">{avisoProductos}</Alert>
            ) : null}

            <CardTableArea>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableHeaderCell>Producto</TableHeaderCell>
                    <TableHeaderCell align="right">Unidades</TableHeaderCell>
                    <TableHeaderCell align="right">Órdenes</TableHeaderCell>
                    <TableHeaderCell align="right">Ingresos</TableHeaderCell>
                    <TableHeaderCell align="right">Ticket promedio</TableHeaderCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {filasProductos.map((fila) => (
                    <TableRow key={fila.id}>
                      <TableCell className="font-medium text-ink" title={fila.nombre}>
                        {fila.nombre}
                      </TableCell>
                      <TableCell align="right" className="tabular">
                        {formatCount(fila.cantidad)}
                      </TableCell>
                      <TableCell align="right" className="tabular">
                        {formatCount(fila.ordenes)}
                      </TableCell>
                      <TableCell align="right" className="tabular">
                        {formatNumber(fila.ingresos)}
                      </TableCell>
                      <TableCell align="right" className="tabular">
                        {fila.ticketPromedio}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>

                <tfoot>
                  <TableRow className="border-t border-line bg-surface-muted/60">
                    <TableCell className="font-semibold text-ink">
                      {countLabel(
                        productos.totales.productosDistintos,
                        'producto distinto',
                        'productos distintos',
                      )}
                    </TableCell>
                    <TableCell align="right" className="tabular font-semibold text-ink">
                      {formatCount(productos.totales.cantidad)}
                    </TableCell>
                    <TableCell />
                    <TableCell align="right" className="tabular font-semibold text-ink">
                      {formatNumber(productos.totales.ingresos)}
                    </TableCell>
                    <TableCell />
                  </TableRow>
                </tfoot>
              </Table>
            </CardTableArea>

            <p className="text-micro text-ink-faint">
              Los totales del pie son los del backend para TODO el periodo (
              {countLabel(productos.ordenesAnalizadas, 'orden analizada', 'órdenes analizadas')}), no
              la suma de las filas de arriba: el ranking se corta en {limite} productos. El ticket
              promedio es de cada producto, así que no hay un total que sumar.
            </p>
          </div>
        ) : null}
      </ChartCard>

      {/* 5 ------------------------------------------------- Por qué no cerró --- */}
      <ChartCard
        title="Por qué no cerró"
        description={
          <>
            <span className="inline-flex items-center gap-1.5">
              <TrendingDown size={14} aria-hidden="true" />
              Las conversaciones del periodo que NUNCA llegaron a una orden, con el estado que les
              puso el CRM y el motivo que quedó guardado.
            </span>
          </>
        }
        loading={motivosQuery.isPending && !motivos}
        error={motivosQuery.isError ? getErrorMessage(motivosQuery.error) : null}
        onRetry={() => void motivosQuery.refetch()}
        height={260}
        empty={
          motivos && motivos.totalSinOrden === 0
            ? {
                title: 'Todas las conversaciones del periodo llegaron a una orden',
                description:
                  'No hay nada sin cerrar en estas fechas: el embudo del periodo no perdió ninguna conversación por el camino.',
              }
            : null
        }
      >
        {motivos ? (
          <div className="space-y-6">
            <section
              aria-label="Conversaciones sin orden del periodo"
              className="grid gap-4 sm:grid-cols-2 xl:grid-cols-4"
            >
              <KpiCard
                label="Conversaciones sin orden"
                value={formatCount(motivos.totalSinOrden)}
                hint="Nunca llegaron a tener una orden creada en el periodo"
                tone="warning"
              />
              <KpiCard
                label="Motivos registrados"
                value={formatCount(motivos.motivos.length)}
                hint="Filas de la tabla de motivos, «Sin clasificar» incluida"
              />
            </section>

            <div className="grid gap-6 lg:grid-cols-3">
              <section>
                <h4 className="mb-2 text-caption font-semibold text-ink">
                  Cómo quedaron esos leads
                </h4>
                <CardTableArea>
                  <Table>
                    <TableHead>
                      <TableRow>
                        <TableHeaderCell>Estado</TableHeaderCell>
                        <TableHeaderCell align="right">Conversaciones</TableHeaderCell>
                        <TableHeaderCell align="right">% de las que no cerraron</TableHeaderCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {filasEstadoSinOrdenReporte(
                        motivos.porEstadoLead,
                        motivos.totalSinOrden,
                      ).map((fila) => (
                        <TableRow key={fila.id}>
                          <TableCell>
                            <Badge tone={fila.tone} dot>
                              {fila.label}
                            </Badge>
                          </TableCell>
                          <TableCell align="right" className="tabular">
                            {formatCount(fila.total)}
                          </TableCell>
                          <TableCell align="right" className="tabular">
                            {fila.porcentaje}
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </CardTableArea>
                <p className="mt-2 text-micro text-ink-faint">
                  Ese «%» lo calcula esta pantalla sobre las conversaciones sin orden del periodo.
                </p>
              </section>

              <section className="lg:col-span-2">
                <h4 className="mb-2 text-caption font-semibold text-ink">
                  Motivos que guardó el CRM
                </h4>
                <CardTableArea>
                  <Table>
                    <TableHead>
                      <TableRow>
                        <TableHeaderCell>Estado</TableHeaderCell>
                        <TableHeaderCell>Motivo</TableHeaderCell>
                        <TableHeaderCell align="right">Conversaciones</TableHeaderCell>
                        <TableHeaderCell align="right">% sin orden</TableHeaderCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {filasMotivos.map((fila) => (
                        <TableRow key={fila.id}>
                          <TableCell>
                            <Badge tone={fila.tone} dot>
                              {fila.estadoLabel}
                            </Badge>
                          </TableCell>
                          <TableCell>{fila.motivo}</TableCell>
                          <TableCell align="right" className="tabular">
                            {formatCount(fila.total)}
                          </TableCell>
                          <TableCell align="right" className="tabular">
                            {fila.porcentaje}
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </CardTableArea>
                <p className="mt-2 text-micro text-ink-faint">
                  El motivo lo pone el CRM al reclasificar un lead; «Sin clasificar en el CRM»
                  significa que esa conversación todavía no tiene uno. Los porcentajes son los del
                  backend, sobre las {formatCount(motivos.totalSinOrden)} conversaciones sin orden.
                </p>
              </section>
            </div>
          </div>
        ) : null}
      </ChartCard>

      {/* 6 -------------------------------------------- Horarios de atención --- */}
      <ChartCard
        title="Horarios de atención"
        description={
          <>
            <span className="inline-flex items-center gap-1.5">
              <Clock size={14} aria-hidden="true" />
              Cuándo escribe el cliente y cuándo entra el lead, contados en la zona del negocio (
              {horarios?.zonaHoraria ?? zonaHoraria}): el mapa 7×24 y, debajo, las franjas con más
              actividad.
            </span>
          </>
        }
        actions={
          <Select
            aria-label="Qué se intensifica en el mapa"
            className="w-60"
            value={campoHorario}
            onChange={(event) => setParam(PARAM_HORARIO, event.target.value)}
          >
            {CAMPOS_HORARIO.map((opcion) => (
              <option key={opcion.value} value={opcion.value}>
                {opcion.label}
              </option>
            ))}
          </Select>
        }
        loading={horariosQuery.isPending && !horarios}
        error={horariosQuery.isError ? getErrorMessage(horariosQuery.error) : null}
        onRetry={() => void horariosQuery.refetch()}
        height={300}
        empty={
          horarios &&
          horarios.totales.mensajes === 0 &&
          horarios.totales.conversacionesIniciadas === 0
            ? {
                title: 'Ninguna hora del periodo tuvo actividad',
                description:
                  'No llegaron mensajes del cliente ni conversaciones nuevas en estas fechas, así que el mapa y las franjas quedarían vacíos.',
              }
            : null
        }
      >
        {horarios ? (
          <div className="space-y-6">
            {horarios.truncado ? (
              <Alert tone="warning">
                El backend alcanzó el tope de filas del periodo: el mapa se dibuja con lo que alcanzó
                a contar, así que las horas con más tráfico pueden estar incompletas.
              </Alert>
            ) : null}

            <HeatmapGrid
              matriz={horarios.matriz}
              campo={campoHorario}
              footer={`La hora más movida del periodo: ${describirPico(
                horarios.picos[campoHorario],
                campoHorario,
              )}`}
            />

            <CardTableArea>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableHeaderCell>Día</TableHeaderCell>
                    <TableHeaderCell>Hora</TableHeaderCell>
                    <TableHeaderCell align="right">Actividad</TableHeaderCell>
                    <TableHeaderCell align="right">% del mapa</TableHeaderCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {franjasPico.map((fila) => (
                    <TableRow key={fila.id}>
                      <TableCell>{fila.dia}</TableCell>
                      <TableCell className="tabular">{fila.hora}</TableCell>
                      <TableCell align="right" className="tabular">
                        {formatCount(fila.valor)}
                      </TableCell>
                      <TableCell align="right" className="tabular">
                        {fila.porcentaje}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </CardTableArea>

            <p className="text-micro text-ink-faint">
              Las franjas de la tabla son las más movidas para el conteo elegido; el «% del mapa» lo
              calcula esta pantalla sobre la suma del propio mapa. En el periodo entraron{' '}
              {formatCount(horarios.totales.mensajes)} mensajes del cliente y{' '}
              {formatCount(horarios.totales.conversacionesIniciadas)} conversaciones nuevas.
            </p>
          </div>
        ) : null}
      </ChartCard>

      <footer className="flex flex-wrap items-start gap-2 text-micro text-ink-faint">
        <Users size={14} aria-hidden="true" className="mt-0.5 shrink-0" />
        <span>
          Los seis bloques salen del mismo módulo que alimenta el{' '}
          <Link to={enlacePanel} className="font-medium text-brand-700 hover:underline">
            panel
          </Link>
          , con los mismos filtros en la URL: un enlace compartido abre las dos pantallas en el mismo
          periodo. Las filas y los avisos son los del backend; esta pantalla solo calcula tres
          porcentajes, y cada uno está rotulado donde aparece.
        </span>
      </footer>
    </div>
  );
}
